import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LanguageSwitcher } from "../components/app/LanguageSwitcher";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success(t("auth.welcomeBackToast"));
      navigate("/dashboard");
    } catch (err) {
      // FastAPI 422s return `detail` as an array of validation objects; render
      // a friendly string instead of letting React crash on the array.
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d?.msg).filter(Boolean).join(", ")
        : (typeof detail === "string" ? detail : "");
      toast.error(msg || t("auth.loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#f9f9f8]" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-green-800 to-green-900 p-12 text-white items-center">
        <div className="max-w-md">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="w-9 h-9 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center">
              <Sparkles className="w-4 h-4" />
            </span>
            <span className="font-display font-bold text-xl">Tasklio AI</span>
          </Link>
          <h2 className="font-display mt-16 text-4xl font-semibold tracking-tight leading-tight">
            {t("auth.loginSidebarTitle")}
          </h2>
          <p className="mt-5 text-green-50 leading-relaxed">
            {t("auth.loginSidebarBody")}
          </p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-8">
            <Link to="/" className="lg:hidden inline-flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-green-800 text-white flex items-center justify-center">
                <Sparkles className="w-4 h-4" />
              </span>
              <span className="font-display font-bold text-lg">Tasklio AI</span>
            </Link>
            <div className="ml-auto">
              <LanguageSwitcher testidPrefix="login-lang-switch" />
            </div>
          </div>
          <h1 className="font-display text-3xl font-semibold text-gray-900">{t("auth.loginTitle")}</h1>
          <p className="mt-2 text-gray-500">{t("auth.loginSubtitle")}</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t("auth.emailPlaceholder")}
                required
                className="mt-2 h-11"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-green-800 hover:underline font-medium"
                  data-testid="login-forgot-password-link"
                >
                  {t("auth.forgotPassword")}
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 h-11"
                data-testid="login-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-green-800 hover:bg-green-900 text-white"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("auth.loginButton")}
            </Button>
          </form>

          <p className="mt-8 text-sm text-gray-500 text-center">
            {t("auth.noAccount")}{" "}
            <Link to="/register" className="text-green-800 hover:underline font-medium" data-testid="login-register-link">
              {t("auth.signUpLink")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
